import type { ReactNode } from "react";
import { toastManager } from "@volca/ui/lib/toast-manager";

export type ToastOptions = {
  id?: string;
  description?: ReactNode;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
};

type ToastType = "success" | "error" | "info" | "warning" | "loading";

const DEFAULT_DURATION_MS = 5_000;

const toTimeout = (duration: number | undefined) => {
  if (duration === Infinity) {
    return 0;
  }
  if (duration === undefined) {
    return DEFAULT_DURATION_MS;
  }
  return duration;
};

const toAddOptions = (
  title: ReactNode,
  type: ToastType,
  options?: ToastOptions,
) => ({
  id: options?.id,
  title,
  description: options?.description,
  type,
  timeout: toTimeout(options?.duration),
  actionProps: options?.action
    ? {
        children: options.action.label,
        onClick: options.action.onClick,
      }
    : undefined,
});

const show = (title: ReactNode, type: ToastType, options?: ToastOptions) =>
  toastManager.add(toAddOptions(title, type, options));

const toastFn = Object.assign(
  (message: ReactNode, options?: ToastOptions) =>
    show(message, "info", options),
  {
    success: (message: ReactNode, options?: ToastOptions) =>
      show(message, "success", options),
    error: (message: ReactNode, options?: ToastOptions) =>
      show(message, "error", options),
    info: (message: ReactNode, options?: ToastOptions) =>
      show(message, "info", options),
    warning: (message: ReactNode, options?: ToastOptions) =>
      show(message, "warning", options),
    loading: (message: ReactNode, options?: ToastOptions) =>
      show(message, "loading", options),
    dismiss: (id?: string) => toastManager.close(id),
    promise: <Value>(
      promise: Promise<Value>,
      messages: {
        loading: ReactNode;
        success: ReactNode | ((value: Value) => ReactNode);
        error: ReactNode | ((error: unknown) => ReactNode);
      },
    ) =>
      toastManager.promise(promise, {
        loading:
          typeof messages.loading === "string"
            ? messages.loading
            : { title: messages.loading, type: "loading" },
        success: (value) => {
          const message =
            typeof messages.success === "function"
              ? messages.success(value)
              : messages.success;
          return typeof message === "string"
            ? message
            : { title: message, type: "success" };
        },
        error: (error) => {
          const message =
            typeof messages.error === "function"
              ? messages.error(error)
              : messages.error;
          return typeof message === "string"
            ? message
            : { title: message, type: "error" };
        },
      }),
  },
);

export { toastFn as toast };
